<template>
  <teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[1000] overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="$emit('close')"></div>

      <!-- Modal -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div
          class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl transform transition-all"
          @click.stop
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
          >
            <div>
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Actie toewijzen</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ prospect?.organization_name }} - {{ prospect?.subject }}
              </p>
            </div>
            <button
              @click="$emit('close')"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="px-6 py-4 space-y-6">
            <!-- Success message -->
            <div
              v-if="successMessage"
              class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
            >
              <div class="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircleIcon class="h-5 w-5" />
                <span>{{ successMessage }}</span>
              </div>
            </div>

            <!-- Action Type Selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Actie type <span class="text-red-500">*</span>
              </label>
              <Listbox v-model="selectedActionType" v-slot="{ open: actionTypeOpen }">
                <div class="relative">
                  <ListboxButton
                    class="relative w-full cursor-pointer bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-left border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
                    :class="
                      actionTypeOpen
                        ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                        : 'rounded-lg'
                    "
                  >
                    <span class="block truncate">
                      {{ selectedActionType?.name || 'Selecteer actie type...' }}
                    </span>
                    <span
                      class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </span>
                  </ListboxButton>

                  <transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                  >
                    <ListboxOptions
                      class="absolute z-50 max-h-96 w-full overflow-auto rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 shadow-lg focus:outline-none"
                    >
                      <ListboxOption
                        v-for="type in actionTypes"
                        :key="type.id"
                        :value="type"
                        v-slot="{ active, selected }"
                        class="cursor-pointer select-none"
                      >
                        <div
                          :class="[
                            active
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200'
                              : 'text-gray-900 dark:text-white',
                            'relative py-3 pl-4 pr-10'
                          ]"
                        >
                          <span
                            :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                          >
                            {{ type.name }}
                          </span>
                          <span
                            v-if="type.description"
                            class="text-xs text-gray-500 dark:text-gray-400 block mt-0.5"
                          >
                            {{ type.description }}
                          </span>
                          <span
                            v-if="selected"
                            class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                          >
                            <CheckIcon class="h-5 w-5" />
                          </span>
                        </div>
                      </ListboxOption>
                    </ListboxOptions>
                  </transition>
                </div>
              </Listbox>
            </div>

            <!-- Sub-actions (displayed as list, not toggleable) -->
            <div v-if="selectedActionType && selectedActionType.items?.length > 0">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sub-acties
              </label>
              <div
                class="space-y-2 border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
              >
                <div
                  v-for="item in selectedActionType.items"
                  :key="item.id"
                  class="flex items-center gap-3 p-2 rounded"
                >
                  <div class="h-2 w-2 bg-blue-500 rounded-full"></div>
                  <span class="text-sm text-gray-700 dark:text-gray-200">{{ item.name }}</span>
                </div>
              </div>
            </div>

            <!-- Assignee - Searchable dropdown -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ isDealNoDeal ? 'Toewijzen aan commerciant' : 'Toewijzen aan' }}
                <span class="text-red-500">*</span>
              </label>
              <Combobox v-model="selectedEmployee">
                <div class="relative">
                  <div class="relative w-full">
                    <ComboboxInput
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :class="
                        employeeDropdownOpen
                          ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                          : 'rounded-lg'
                      "
                      :displayValue="employee => employee?.name || ''"
                      @change="employeeQuery = $event.target.value"
                      placeholder="Zoek een medewerker..."
                    />
                    <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </ComboboxButton>
                  </div>

                  <transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                    @before-enter="employeeDropdownOpen = true"
                    @after-leave="employeeDropdownOpen = false"
                  >
                    <ComboboxOptions
                      class="absolute z-50 max-h-96 w-full overflow-auto rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 shadow-lg focus:outline-none"
                    >
                      <div
                        v-if="filteredEmployees.length === 0 && employeeQuery !== ''"
                        class="relative cursor-default select-none py-3 px-4 text-gray-500 dark:text-gray-400"
                      >
                        Geen medewerkers gevonden
                      </div>

                      <ComboboxOption
                        v-for="employee in filteredEmployees"
                        :key="employee.simplicate_id"
                        :value="employee"
                        v-slot="{ active, selected }"
                        class="cursor-pointer select-none"
                      >
                        <div
                          :class="[
                            active
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200'
                              : 'text-gray-900 dark:text-white',
                            'relative py-3 pl-4 pr-10'
                          ]"
                        >
                          <span
                            :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                          >
                            {{ employee.name }}
                          </span>
                          <span
                            v-if="employee.role"
                            class="text-xs text-gray-500 dark:text-gray-400 block mt-0.5"
                          >
                            {{ employee.role }}
                          </span>
                          <span
                            v-if="selected"
                            class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                          >
                            <CheckIcon class="h-5 w-5" />
                          </span>
                        </div>
                      </ComboboxOption>
                    </ComboboxOptions>
                  </transition>
                </div>
              </Combobox>
            </div>

            <!-- PL Assignee - Only for deal_no_deal -->
            <div v-if="isDealNoDeal">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Toewijzen aan performance lead <span class="text-red-500">*</span>
              </label>
              <Combobox v-model="selectedPL">
                <div class="relative">
                  <div class="relative w-full">
                    <ComboboxInput
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :class="
                        plDropdownOpen
                          ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                          : 'rounded-lg'
                      "
                      :displayValue="employee => employee?.name || ''"
                      @change="plQuery = $event.target.value"
                      placeholder="Zoek een performance lead..."
                    />
                    <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </ComboboxButton>
                  </div>

                  <transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                    @before-enter="plDropdownOpen = true"
                    @after-leave="plDropdownOpen = false"
                  >
                    <ComboboxOptions
                      class="absolute z-50 max-h-96 w-full overflow-auto rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 shadow-lg focus:outline-none"
                    >
                      <div
                        v-if="filteredPLEmployees.length === 0 && plQuery !== ''"
                        class="relative cursor-default select-none py-3 px-4 text-gray-500 dark:text-gray-400"
                      >
                        Geen medewerkers gevonden
                      </div>

                      <ComboboxOption
                        v-for="employee in filteredPLEmployees"
                        :key="employee.simplicate_id"
                        :value="employee"
                        v-slot="{ active, selected }"
                        class="cursor-pointer select-none"
                      >
                        <div
                          :class="[
                            active
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200'
                              : 'text-gray-900 dark:text-white',
                            'relative py-3 pl-4 pr-10'
                          ]"
                        >
                          <span
                            :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                          >
                            {{ employee.name }}
                          </span>
                          <span
                            v-if="employee.role"
                            class="text-xs text-gray-500 dark:text-gray-400 block mt-0.5"
                          >
                            {{ employee.role }}
                          </span>
                          <span
                            v-if="selected"
                            class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                          >
                            <CheckIcon class="h-5 w-5" />
                          </span>
                        </div>
                      </ComboboxOption>
                    </ComboboxOptions>
                  </transition>
                </div>
              </Combobox>
            </div>

            <!-- Due Date -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Deadline <span class="text-red-500">*</span>
              </label>
              <input
                :value="displayDate"
                @input="handleDateInput"
                type="text"
                placeholder="dd/mm/jjjj"
                maxlength="10"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Beschrijving / Opmerkingen
              </label>
              <textarea
                v-model="description"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Eventuele extra informatie voor de actie..."
              ></textarea>
            </div>

            <!-- Error message -->
            <div
              v-if="errorMessage"
              class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4"
            >
              <p class="text-red-700 dark:text-red-400 text-sm">{{ errorMessage }}</p>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
          >
            <button
              @click="$emit('close')"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Annuleren
            </button>
            <button
              @click="createAction"
              :disabled="!canSubmit || isSubmitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span v-if="isSubmitting">Aanmaken...</span>
              <span v-else>Actie aanmaken</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption
} from '@headlessui/vue'
import { XMarkIcon, CheckIcon, ChevronUpDownIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import { useActionAssignmentForm } from '@/composables/modals/useActionAssignmentForm.js'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  prospect: {
    type: Object,
    default: () => ({})
  },
  defaultAssignee: {
    type: Object,
    default: () => ({ name: '', email: '' })
  }
})

const emit = defineEmits(['close', 'created'])

const {
  actionTypes,
  selectedActionType,
  selectedEmployee,
  employeeQuery,
  employeeDropdownOpen,
  selectedPL,
  plQuery,
  plDropdownOpen,
  dueDate: _dueDate,
  description,
  isSubmitting,
  errorMessage,
  successMessage,
  filteredEmployees,
  isDealNoDeal,
  filteredPLEmployees,
  canSubmit,
  displayDate,
  handleDateInput,
  createAction
} = useActionAssignmentForm(props, emit)
</script>
